/**
 * usePreloadInitialRoomAssets — Preloads assets needed for the initial room selection.
 *
 * Runs on mount. Loads:
 * - Both room background images (needed for the room selection UI)
 * - Both room background videos (we don't know which room the user will pick)
 *
 * Videos are enqueued at high priority since the user has ~10 seconds of lottie
 * animation before one of them is needed. If both finish in time, great. If not,
 * the selected room's video will still load on demand via StrapiMedia.
 */

import { useEffect, useRef } from "react";
import { usePhases } from "@/state/cacheStoreHooks";
import { preloadManager } from "./PreloadManager";
import { StrapiAsset } from "@/types/api";

export function usePreloadIntroAssets(): void {
    const phases = usePhases();
    const triggeredRef = useRef(false);

    useEffect(() => {
        try {
            const firstPhase = phases[0];
            if (!firstPhase) return;

            if (triggeredRef.current) return;
            triggeredRef.current = true;
            const roomImages: StrapiAsset[] = [];

            if (firstPhase.room_a?.image) {
                roomImages.push(firstPhase.room_a.image);
            }
            if (firstPhase.room_b?.image) {
                roomImages.push(firstPhase.room_b.image);
            }

            if (roomImages.length > 0) {
                preloadManager.enqueueImages(roomImages, "lg", "critical");
            }

            // Adjust field name to your schema (e.g. room_a?.animated, room_a?.background_video)
            const roomAVideo = firstPhase.room_a?.animated;
            const roomBVideo = firstPhase.room_b?.animated;

            if (roomAVideo) preloadManager.enqueueVideo(roomAVideo, "high");
            if (roomBVideo) preloadManager.enqueueVideo(roomBVideo, "high");
        } catch (err) {
            // Preload failures must never break gameplay — log and move on
            console.warn("Failed to enqueue initial room assets:", err);
        }
    }, [phases]);
}